/**
 * Trunnion AI - Chat Input
 * Multi-line text input with classification selector and send button.
 */

import { useState, useRef, useCallback, type KeyboardEvent, type FormEvent } from 'react';
import { Send, StopCircle, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface ChatInputProps {
  onSend: (message: string, classificationLevel: string) => void;
  onCancel?: () => void;
  isStreaming?: boolean;
  disabled?: boolean;
}

const CLASSIFICATION_OPTIONS = [
  { value: 'unclassified', label: 'UNCLASSIFIED', color: 'text-green-400' },
  { value: 'cui', label: 'CUI', color: 'text-yellow-400' },
  { value: 'secret', label: 'SECRET', color: 'text-red-400' },
  { value: 'top_secret', label: 'TOP SECRET', color: 'text-orange-400' },
];

export function ChatInput({ onSend, onCancel, isStreaming, disabled }: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [classification, setClassification] = useState('unclassified');
  const [showClassMenu, setShowClassMenu] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = useCallback(
    (e?: FormEvent) => {
      e?.preventDefault();
      const trimmed = message.trim();
      if (!trimmed || isStreaming || disabled) return;

      onSend(trimmed, classification);
      setMessage('');

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    },
    [message, classification, isStreaming, disabled, onSend],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit],
  );

  const handleInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, []);

  const selectedClass = CLASSIFICATION_OPTIONS.find((c) => c.value === classification);

  return (
    <div className="border-t border-trunnion-slate-700 bg-trunnion-slate-900 p-4">
      <form onSubmit={handleSubmit} className="flex items-end gap-3">
        {/* Classification selector */}
        <div className="relative">
          <button
            type="button"
            onClick={() => setShowClassMenu(!showClassMenu)}
            className={`flex items-center gap-1 rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 px-2.5 py-2 text-xs font-medium ${selectedClass?.color ?? 'text-green-400'} hover:bg-trunnion-slate-700 transition-colors`}
          >
            <span>{selectedClass?.label ?? 'U'}</span>
            <ChevronDown className="h-3 w-3" />
          </button>

          {showClassMenu && (
            <div className="absolute bottom-full left-0 mb-1 w-40 rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800 py-1 shadow-xl z-50">
              {CLASSIFICATION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    setClassification(opt.value);
                    setShowClassMenu(false);
                  }}
                  className={`flex w-full items-center px-3 py-1.5 text-xs ${opt.color} hover:bg-trunnion-slate-700`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Text input */}
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onInput={handleInput}
          placeholder="Send a message..."
          rows={1}
          disabled={disabled}
          className="flex-1 resize-none rounded-lg border border-trunnion-slate-600 bg-trunnion-slate-800 px-4 py-2.5 text-sm text-white placeholder-trunnion-slate-500 focus:border-trunnion-gold-500 focus:outline-none focus:ring-1 focus:ring-trunnion-gold-500/30 disabled:opacity-50"
        />

        {/* Send / Cancel button */}
        {isStreaming ? (
          <Button
            type="button"
            variant="danger"
            size="sm"
            onClick={onCancel}
          >
            <StopCircle className="h-4 w-4" />
          </Button>
        ) : (
          <Button
            type="submit"
            size="sm"
            disabled={!message.trim() || disabled}
          >
            <Send className="h-4 w-4" />
          </Button>
        )}
      </form>

      <p className="mt-1.5 text-xs text-trunnion-slate-500">
        Press Enter to send, Shift+Enter for new line
      </p>
    </div>
  );
}
